import { useState } from 'react'
import { Eye } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { InvoiceDetailModal } from '@/components/billing/invoice-detail-modal'
import { useInvoices } from '@/hooks/use-billing'
import { formatKES } from '@/lib/utils'
import type { Invoice, InvoiceStatus, InvoiceType } from '@riverpark/types'

function StatusBadge({ status }: { status: InvoiceStatus }) {
  switch (status) {
    case 'paid':
      return <Badge variant="paid">Paid</Badge>
    case 'overdue':
      return <Badge variant="overdue">Overdue</Badge>
    case 'sent':
      return <Badge variant="pending">Sent</Badge>
    case 'voided':
      return <Badge variant="default">Voided</Badge>
    default:
      return <Badge variant="draft">Draft</Badge>
  }
}

function InvoiceTypeBadge({ invoiceType }: { invoiceType: InvoiceType }) {
  if (invoiceType === 'supplementary') return <Badge variant="overdue">Supplementary</Badge>
  return null
}

function formatDate(unixSeconds?: number) {
  if (!unixSeconds) return '—'
  return new Date(unixSeconds * 1000).toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function AmountCell({ invoice }: { invoice: Invoice }) {
  const isPartial =
    invoice.amountPaid > 0 && invoice.status !== 'paid' && invoice.status !== 'voided'
  if (!isPartial) return <>{formatKES(invoice.amount)}</>
  return (
    <span>
      {formatKES(invoice.balance)} due{' '}
      <span className="text-xs text-muted-foreground">of {formatKES(invoice.amount)}</span>
    </span>
  )
}

interface UtilityGroup {
  utilityTypeId: string | null
  utilityTypeName: string
  invoices: Invoice[]
  balance: number
}

/** Groups in the order their utility type first appears — invoices already arrive sorted
 * newest-first, so this naturally surfaces the most recently active utility type first. */
function groupByUtilityType(invoices: Invoice[]): UtilityGroup[] {
  const groups: UtilityGroup[] = []
  const byKey = new Map<string, UtilityGroup>()
  for (const inv of invoices) {
    const key = inv.utilityTypeId ?? 'none'
    let group = byKey.get(key)
    if (!group) {
      group = {
        utilityTypeId: inv.utilityTypeId,
        utilityTypeName: inv.utilityTypeName ?? 'Other',
        invoices: [],
        balance: 0,
      }
      byKey.set(key, group)
      groups.push(group)
    }
    group.invoices.push(inv)
    if (inv.status !== 'paid' && inv.status !== 'voided') group.balance += inv.balance
  }
  return groups
}

/**
 * Grouped by utility type, same as the member-facing Bills page — this is the "open the member,
 * see everything they owe" view reached from the Arrears tab. No pay button and no pagination
 * (unlike that page): staff aren't paying from here, and grouping needs the full set of a
 * member's invoices in hand at once, not a server-paginated slice that could split a utility
 * type's invoices across pages.
 */
export function MemberInvoicesTab({ memberId }: { memberId: string }) {
  const [activeInvoice, setActiveInvoice] = useState<Invoice | null>(null)

  const { data, isLoading } = useInvoices({
    memberId,
    limit: 100,
    excludeInvoiceType: 'credit_note',
  })
  const invoices = data?.data ?? []

  if (isLoading) {
    return <p className="font-sans text-sm text-muted-foreground">Loading…</p>
  }
  if (invoices.length === 0) {
    return (
      <p className="rounded border border-border-2 px-4 py-6 text-center font-sans text-sm text-muted-foreground">
        No invoices yet.
      </p>
    )
  }

  const groups = groupByUtilityType(invoices)

  return (
    <div className="space-y-5">
      {groups.map((group) => (
        <div key={group.utilityTypeId ?? 'none'}>
          <p className="mb-2 font-sans text-sm font-medium text-ink">
            {group.utilityTypeName}
            <span className="ml-2 font-normal text-muted-foreground">
              {group.balance > 0 ? `${formatKES(group.balance)} due` : 'Cleared'}
            </span>
          </p>

          <div className="overflow-x-auto rounded border border-border-2">
            <table className="w-full font-sans text-sm">
              <thead>
                <tr className="border-b border-border-2 bg-cream-2 text-left text-label uppercase tracking-[0.08em] text-muted-foreground">
                  <th className="px-4 py-3">Invoice #</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Due</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {group.invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-border-2 last:border-0">
                    <td className="px-4 py-3 font-medium text-ink">{inv.invoiceNumber}</td>
                    <td className="px-4 py-3 text-ink">
                      <AmountCell invoice={inv} />
                    </td>
                    <td className="px-4 py-3 text-ink">{formatDate(inv.dueDate)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <StatusBadge status={inv.status} />
                        <InvoiceTypeBadge invoiceType={inv.invoiceType} />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="secondary" size="sm" onClick={() => setActiveInvoice(inv)}>
                        <Eye size={14} className="mr-1.5" />
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {activeInvoice && (
        <InvoiceDetailModal invoice={activeInvoice} onClose={() => setActiveInvoice(null)} />
      )}
    </div>
  )
}
